import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CustomError } from '../errors/CustomError';
import { ActivityActionType } from '../types/enums';
import type { JsonValue } from 'type-fest';
import { COUNTRIES, type CountryId } from '@/lib/types/enums';
import { parse } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatNumber = (num: number, decimals?: number): string => {
  const options = decimals !== undefined ? {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  } : {};
  
  return new Intl.NumberFormat('en-US', options).format(num);
};

export const formatToNumber = (num: number): number => {
  return Number(num);
};

export const formatDateTime = (date: string | Date | null): string => {
  if (!date) return '';

  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(new Date(date));
  } catch {
    return '';
  }
};

export const formatDate = (date: Date) => {
  if (!date) return null; // Handle null or undefined dates
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Parses a date string without timezone assumptions
 * Treats the input as local time (no UTC conversion)
 * 
 * @param {string} dateString - Date string in format 'YYYY-MM-DDTHH:mm:ss'
 * @returns {Date|null} - Date object representing local time, or null if parsing fails
 */
export function parseLocalDateTime(dateString) {
  if (!dateString) {
    return null;
  }
  
  // Handle different possible formats
  const formats = [
    "yyyy-MM-dd'T'HH:mm:ss",     // 2025-07-16T00:00:00
    "yyyy-MM-dd'T'HH:mm:ss.SSS", // 2025-07-16T00:00:00.000
    "yyyy-MM-dd HH:mm:ss",       // 2025-07-16 00:00:00
    "yyyy-MM-dd",                // 2025-07-16 (will default to midnight)
  ];
  
  for (const format of formats) {
    try {
      const result = parse(dateString, format, new Date());
      // Check if parsing was successful (not Invalid Date)
      if (!isNaN(result.getTime())) {
        return result;
      }
    } catch (error) {
      // Continue to next format
      continue;
    }
  }
  
  // If all formats fail, return new Date (string) else return null
  try {
    return new Date(dateString);
  } catch (error) {
    return null;
  }
}

export function isCustomError(error: CustomError): error is CustomError {
  return error instanceof CustomError && typeof error.showToClient === 'boolean';
}

export const convertToTonnes = (weight: string | number | null, unit?: string): number => {
  const numWeight = typeof weight === 'string' ? parseFloat(weight) : weight;
  
  if (!numWeight || isNaN(numWeight)) return 0;
 
  switch(unit?.toUpperCase()) {
    case 'LB': 
    case 'LBS':
      return numWeight * 0.000453592;
    case 'KG': 
    case 'KGS': 
      return numWeight * 0.001;
    default: return numWeight;
  }
 };

// Helper function to get country by ID
export const getCountryById = (id: CountryId) => 
  COUNTRIES.find(country => country.id === id);

// Helper function to get country by code
export const getCountryByCode = (code: string) => 
  COUNTRIES.find(country => country.code === code);

// Helper function to validate email format
//export const isValidEmail = (email : string) => /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email);
export const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

//If inviet not sent then only we can send invite
export const canSendInvite = (status: string): boolean => {
  return status === 'INVITE_NOT_SENT' || status === 'INVITE_SENT' || status === 'PENDING_RESPONSE';
};

export const toTitleCase = (str: string): string => {
  return str == '' || str == null
    ? ''
    : str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

// Transform string to JSON
export const transformJsonValue = <T>(json: JsonValue): T => {
  if (typeof json === 'string') {
    return JSON.parse(json) as T;
  }
  return json as T;
 };

export const processOrganizationLogo = (
  file: File,
  maxWidth: number,
  maxHeight: number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Calculate dimensions for the image
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          const scaleFactor = Math.min(maxWidth / width, maxHeight / height);
          width = width * scaleFactor;
          height = height * scaleFactor;
        }

        // Resize the canvas to the new dimensions
        canvas.width = width;
        canvas.height = height;

        // Draw the image onto the canvas
        ctx?.drawImage(img, 0, 0, width, height);

        // Convert canvas to Base64 string
        const base64String = canvas.toDataURL('image/png'); // You can use 'image/jpeg' too
        resolve(base64String);
      };

      img.onerror = () => reject(new Error('Image processing failed'));
      img.src = event.target?.result as string;
    };

    reader.onerror = () => reject(new Error('File reading failed'));
    reader.readAsDataURL(file); // Start reading the file
  });
};

export const PLAN_CONFIG = {
  Free: {
    prices: {
      monthly: 0,
      yearly: 0
    },
    limits: {
      organizations: 1,
      projects: 2,
      users: 5,
      totalParticipants: 50,
      scenariosPerProject: 3,
      customQuestions: 20,
      emailSupport: true,
      advancedAnalytics: false,
      customBranding: false,
      prioritySupport: false,
      dedicatedAccountManager: false,
      customIntegration: false
    },
    stripeIds: {
      monthly: process.env.NEXT_PUBLIC_STRIPE_BASIC_MONTHLY_PRICE_ID,
      yearly: process.env.NEXT_PUBLIC_STRIPE_BASIC_YEARLY_PRICE_ID
    },
    popular: false,
    description: 'For individuals and small teams'
  },
  Gold: {
    prices: {
      monthly: 79.99,
      yearly: 767.88
    },
    limits: {
      organizations: 3,
      projects: 10,
      users: 10,
      totalParticipants: 1000,
      scenariosPerProject: 5,
      customQuestions: 100,
      emailSupport: true,
      advancedAnalytics: true,
      customBranding: true,
      prioritySupport: true,
      dedicatedAccountManager: false,
      customIntegration: false
    },
    stripeIds: {
      monthly: process.env.NEXT_PUBLIC_STRIPE_GOLD_MONTHLY_PRICE_ID,
      yearly: process.env.NEXT_PUBLIC_STRIPE_GOLD_YEARLY_PRICE_ID
    },
    popular: true,
    description: 'For growing businesses'
  },
  Platinum: {
    prices: {
      monthly: 199.99,
      yearly: 1919.88
    },
    limits: {
      organizations: 5,
      projects: 20,
      users: 20,
      totalParticipants: 2000,
      scenariosPerProject: 10,
      customQuestions: 250,
      emailSupport: true,
      advancedAnalytics: true,
      customBranding: true,
      prioritySupport: true,
      dedicatedAccountManager: true,
      customIntegration: true
    },
    stripeIds: {
      monthly: process.env.NEXT_PUBLIC_STRIPE_PLATINUM_MONTHLY_PRICE_ID,
      yearly: process.env.NEXT_PUBLIC_STRIPE_PLATINUM_YEARLY_PRICE_ID
    },
    popular: false,
    description: 'For large organizations'
  }
} as const;

// Helper function to get the feature name
export const getFeatureName = (feature: keyof (typeof PLAN_CONFIG)['Free']['limits']): string => {
  switch (feature) {
    case 'organizations':
      return 'Organizations';
    case 'projects':
      return 'Projects';
    case 'users':
      return 'Organization Members';
    case 'totalParticipants':
      return 'Total Participants';
    case 'scenariosPerProject':
      return 'Scenarios per Project';
    case 'customQuestions':
      return 'Custom Questions';
    case 'advancedAnalytics':
      return 'Advanced Analytics';
    case 'customBranding':
      return 'Custom Branding';
    case 'emailSupport':
      return 'Email Support';
    case 'prioritySupport':
      return 'Priority Support';
    case 'dedicatedAccountManager':
      return 'Dedicated Account Manager';
    case 'customIntegration':
      return 'Custom Integration';
    default:
      return feature;
  }
};

// Helper function to get the feature description
// Helper function to get the feature description
export const getFeatureDescription = (
  feature: keyof (typeof PLAN_CONFIG)['Free']['limits']
): string => {
  switch (feature) {
    case 'organizations':
      return 'Number of organizations you can create';
    case 'projects':
      return 'Number of projects you can create';
    case 'users':
      return 'Number of organization members allowed';
    case 'totalParticipants':
      return 'Total number of participants allowed';
    case 'scenariosPerProject':
      return 'Number of scenarios you can create per project';
    case 'customQuestions':
      return 'Number of custom questions you can create';
    case 'advancedAnalytics':
      return 'Access to advanced analytics, insights, and reporting features';
    case 'customBranding':
      return 'Add your own branding, logo, and colors';
    case 'emailSupport':
      return 'Get support via email within 24 hours';
    case 'prioritySupport':
      return 'Priority email and chat support with faster response times';
    case 'dedicatedAccountManager':
      return 'A dedicated account manager to help you succeed';
    case 'customIntegration':
      return 'Custom API integration support and development';
    default:
      return feature;
  }
};
// Helper functions
export const getPlanPrice = (
  planName: keyof typeof PLAN_CONFIG,
  interval: 'monthly' | 'yearly'
): number => {
  return PLAN_CONFIG[planName].prices[interval];
};

export const getPlanStripeId = (
  planName: keyof typeof PLAN_CONFIG,
  interval: 'monthly' | 'yearly'
): string => {
  return PLAN_CONFIG[planName].stripeIds[interval];
};

// Update the getPriceId function to use the config
export const getPriceId = (plan: string, interval: string) => {
  return getPlanStripeId(plan as keyof typeof PLAN_CONFIG, interval as 'monthly' | 'yearly');
};

export const getPlanNameAndIntervalAndPrice = (priceId: string) => {
  for (const [plan, config] of Object.entries(PLAN_CONFIG)) {
    if (priceId === config.stripeIds.monthly || priceId === config.stripeIds.yearly) {
      return {
        plan,
        interval: priceId === config.stripeIds.monthly ? 'monthly' : 'yearly',
        price: config.prices[priceId === config.stripeIds.monthly ? 'monthly' : 'yearly']
      };
    }
  }
};

//Get the plan details
export const getPlanDetails = (priceId: string) => {
  for (const [plan, config] of Object.entries(PLAN_CONFIG)) {
    if (priceId === config.stripeIds.monthly || priceId === config.stripeIds.yearly) {
      return { plan, ...config };
    }
  }
};

// Helper function to convert a date in a specific timezone to UTC
export const convertToUtc = (date: Date, timezone: string): Date => {
  // Use toLocaleString to convert date to the specified timezone
  const options = { timeZone: timezone, hour12: false };
  const localeString = date.toLocaleString('en-US', options);

  // Parse the components of the converted date
  const parts = localeString.split(/[/,\s:]+/); // Split by `/`, `,`, `space`, or `:`
  const month = parseInt(parts[0], 10) - 1; // Months are zero-based in Date
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  const hour = parseInt(parts[3], 10);
  const minute = parseInt(parts[4], 10);
  const second = parseInt(parts[5] || '0', 10); // Handle cases without seconds

  // Return a UTC date
  return new Date(Date.UTC(year, month, day, hour, minute, second));
};

export const transformNullToEmptyString = (data: Record<string, string>) => {
  return Object.entries(data).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: value === null && typeof value === 'string' ? '' : value
    }),
    {}
  );
};

export function getEntityNameFromTable(tableName: string): string {
  return tableName
    .replace(/^vulcan_/, '')
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export function getEntityIdentifier(data: Record<string, any>): string {
  const identifiers = ['name', 'full_name', 'email', 'title', 'code', 'id'];
  for (const identifier of identifiers) {
    if (data[identifier]) {
      return data[identifier];
    }
  }
  return 'record';
}

export function generateActivityDescription(
  action: ActivityActionType,
  tableName: string,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  data?: Record<string, any>,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  result?: Record<string, any>
): string {
  const entityName = getEntityNameFromTable(tableName);
  const entityData = data || result;
  const identifier = entityData ? getEntityIdentifier(entityData) : '';

  switch (action) {
    case ActivityActionType.CREATE:
      return `Created new ${entityName.toLowerCase()}: "${identifier}"`;
    case ActivityActionType.UPDATE:
      return `Updated ${entityName.toLowerCase()}: "${identifier}"`;
    case ActivityActionType.DELETE:
      return `Deleted ${entityName.toLowerCase()}: "${identifier}"`;
    case ActivityActionType.VIEW:
      return `Viewed ${entityName.toLowerCase()}: "${identifier}"`;
    default:
      return `${action} ${entityName.toLowerCase()}: "${identifier}"`;
  }
}
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export function getRelevantResultData(result: Record<string, any>) {
  const relevantFields = [
    'name',
    'full_name',
    'email',
    'status',
    'type',
    'title',
    'code',
    'is_active'
  ];

  return Object.entries(result).reduce(
    (acc, [key, value]) => {
      if (relevantFields.includes(key)) {
        acc[key] = value;
      }
      return acc;
    },
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    {} as Record<string, any>
  );
}

// Helper function to get country code
export function getCountryCode(country: string): string {
    const countryMap: { [key: string]: string } = {
        'United States': 'US',
        'Canada': 'CA',
        'Mexico': 'MX',
        'United Kingdom': 'GB'
    };
    return countryMap[country] || 'US';
}

// Helper function to get timezone based on state
// Helper function to get timezone based on state (one timezone per state)
export function getTimeZone(state: string): string {
    const timezoneMap: { [key: string]: string } = {
        // Pacific Time (UTC-8/-7)
        'CA': 'America/Los_Angeles',      // California
        'WA': 'America/Los_Angeles',      // Washington
        'OR': 'America/Los_Angeles',      // Oregon
        'NV': 'America/Los_Angeles',      // Nevada

        // Mountain Time (UTC-7/-6)
        'MT': 'America/Denver',           // Montana
        'ID': 'America/Denver',           // Idaho
        'WY': 'America/Denver',           // Wyoming
        'UT': 'America/Denver',           // Utah
        'CO': 'America/Denver',           // Colorado
        'NM': 'America/Denver',           // New Mexico
        'AZ': 'America/Phoenix',          // Arizona (no DST)

        // Central Time (UTC-6/-5)
        'ND': 'America/Chicago',          // North Dakota
        'SD': 'America/Chicago',          // South Dakota
        'NE': 'America/Chicago',          // Nebraska
        'KS': 'America/Chicago',          // Kansas
        'OK': 'America/Chicago',          // Oklahoma
        'TX': 'America/Chicago',          // Texas
        'MN': 'America/Chicago',          // Minnesota
        'IA': 'America/Chicago',          // Iowa
        'MO': 'America/Chicago',          // Missouri
        'AR': 'America/Chicago',          // Arkansas
        'LA': 'America/Chicago',          // Louisiana
        'WI': 'America/Chicago',          // Wisconsin
        'IL': 'America/Chicago',          // Illinois
        'MS': 'America/Chicago',          // Mississippi
        'AL': 'America/Chicago',          // Alabama
        'TN': 'America/Chicago',          // Tennessee
        'KY': 'America/Chicago',          // Kentucky
        'IN': 'America/Chicago',          // Indiana
        'MI': 'America/Chicago',          // Michigan

        // Eastern Time (UTC-5/-4)
        'ME': 'America/New_York',         // Maine
        'NH': 'America/New_York',         // New Hampshire
        'VT': 'America/New_York',         // Vermont
        'MA': 'America/New_York',         // Massachusetts
        'RI': 'America/New_York',         // Rhode Island
        'CT': 'America/New_York',         // Connecticut
        'NY': 'America/New_York',         // New York
        'NJ': 'America/New_York',         // New Jersey
        'PA': 'America/New_York',         // Pennsylvania
        'DE': 'America/New_York',         // Delaware
        'MD': 'America/New_York',         // Maryland
        'DC': 'America/New_York',         // District of Columbia
        'VA': 'America/New_York',         // Virginia
        'WV': 'America/New_York',         // West Virginia
        'OH': 'America/New_York',         // Ohio
        'NC': 'America/New_York',         // North Carolina
        'SC': 'America/New_York',         // South Carolina
        'GA': 'America/New_York',         // Georgia
        'FL': 'America/New_York',         // Florida

        // Alaska & Hawaii
        'AK': 'America/Anchorage',        // Alaska
        'HI': 'Pacific/Honolulu',         // Hawaii

        // US Territories
        'PR': 'America/Puerto_Rico',      // Puerto Rico
        'VI': 'America/St_Thomas',        // US Virgin Islands
        'GU': 'Pacific/Guam',             // Guam
        'AS': 'Pacific/Pago_Pago',        // American Samoa
        'MP': 'Pacific/Saipan',           // Northern Mariana Islands
    };

    return timezoneMap[state.toUpperCase()] || 'America/Chicago';
}
